// Port of BivouacEngine/SuitabilityClassifier.swift.

import type { RegulatoryState, SuitabilityStatus } from '../core/models';
import { isBlockingState } from '../core/domain';
import type { PhysicalAssessment } from './suitabilityEvaluator';

/**
 * Presentation rule — regulatory state ALWAYS overrides convenience scoring.
 *
 * Evaluated in this order (first match wins):
 * 1. `restricted` / `prohibited` → `unsuitable` (a perfect meadow never hides a restriction);
 * 2. hard hazard, or terrain data present with score < 0.3 → `unsuitable`;
 * 3. no terrain data, or regulatory `unknown` → `insufficientData` (unknown never becomes green or amber);
 * 4. `official` → `suitable`;
 * 5. `caution`, score < 0.7 or weather score < 0.5 → `caution`;
 * 6. `noKnownRestriction` with score ≥ 0.7 → `suitable`.
 *
 * Comparisons are written as `!(x >= threshold)`, so a NaN score or weather score can never produce green.
 */
export const SuitabilityClassifier = {
  /** Score below which terrain is unsuitable. */
  unsuitableScoreThreshold: 0.3,
  /** Score at or above which terrain can be suitable. */
  suitableScoreThreshold: 0.7,
  /** Weather score below which the status is at most caution. */
  weatherCautionThreshold: 0.5,

  /** Final presentation status. */
  status(
    physical: PhysicalAssessment,
    regulatory: RegulatoryState,
    weatherScore: number | undefined,
  ): SuitabilityStatus {
    const C = SuitabilityClassifier;
    if (isBlockingState(regulatory)) return 'unsuitable';
    if (physical.hasHardHazard) return 'unsuitable';
    if (physical.hasTerrainData && !(physical.score >= C.unsuitableScoreThreshold)) return 'unsuitable';
    if (!physical.hasTerrainData || regulatory === 'unknown') return 'insufficientData';
    if (regulatory === 'official') return 'suitable';
    if (weatherScore !== undefined && !(weatherScore >= C.weatherCautionThreshold)) return 'caution';
    if (regulatory === 'caution' || !(physical.score >= C.suitableScoreThreshold)) return 'caution';
    if (regulatory === 'noKnownRestriction') return 'suitable';
    return 'caution';
  },
};
